import { useEffect, useRef, useState } from 'react';
import { fetchChapter, prefetchChapter } from '../lib/api';
import { step } from '../lib/refs';
import type { Book, ChapterData, Ref } from '../lib/types';

interface Props {
  refValue: Ref;
  books: Book[];
  canClose: boolean;
  onOpenPalette: () => void;
  onAdd: () => void;
  onClose: () => void;
  onStep: (delta: 1 | -1) => void;
  onRandom: () => void;
  onFocus: () => void;
  scrollRef: (el: HTMLDivElement | null) => void;
  onScroll: (el: HTMLDivElement) => void;
}

export function Column({
  refValue,
  books,
  canClose,
  onOpenPalette,
  onAdd,
  onClose,
  onStep,
  onRandom,
  onFocus,
  scrollRef,
  onScroll,
}: Props) {
  const [data, setData] = useState<ChapterData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchChapter(refValue)
      .then((chapter) => {
        if (cancelled) return;
        setData(chapter);
        setLoading(false);
        // Adjacent chapters are the next thing the reader is likely to want.
        prefetchChapter(step(refValue, books, 1));
        prefetchChapter(step(refValue, books, -1));
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refValue.translation, refValue.book, refValue.chapter, books]);

  // Reset to the top on a new chapter, or jump to a requested verse.
  useEffect(() => {
    if (!data) return;
    const body = bodyRef.current;
    if (!body) return;

    if (refValue.verse) {
      const target = body.querySelector<HTMLElement>(`[data-verse="${refValue.verse}"]`);
      if (target) {
        target.scrollIntoView({ block: 'center' });
        return;
      }
    }
    body.scrollTop = 0;
  }, [data, refValue.verse]);

  const heading = data
    ? `${data.book.name} ${data.chapter}`
    : `${books.find((b) => b.id === refValue.book)?.name ?? ''} ${refValue.chapter}`;

  return (
    <section className='column' onPointerEnter={onFocus} onFocusCapture={onFocus}>
      <header className='column__header'>
        <button
          type='button'
          className='title'
          onClick={onOpenPalette}
          title='Find a chapter (⌘P)'
        >
          <span className='title__ref'>{heading}</span>
          <span className='title__translation'>
            {data?.translation.abbr ?? refValue.translation.toUpperCase()}
          </span>
        </button>

        <nav className='controls' aria-label='Chapter controls'>
          <button
            type='button'
            onClick={() => onStep(-1)}
            title='Previous chapter (←)'
            aria-label='Previous chapter'
          >
            <Chevron direction='left' />
          </button>
          <button
            type='button'
            onClick={onRandom}
            title='Random chapter'
            aria-label='Random chapter'
          >
            <Dice />
          </button>
          <button
            type='button'
            onClick={onAdd}
            title='Add a column'
            aria-label='Add a column'
          >
            <Plus />
          </button>
          <button
            type='button'
            onClick={onClose}
            disabled={!canClose}
            title={canClose ? 'Close this column' : 'The last column can’t be closed'}
            aria-label='Close this column'
          >
            <Cross />
          </button>
          <button
            type='button'
            onClick={() => onStep(1)}
            title='Next chapter (→)'
            aria-label='Next chapter'
          >
            <Chevron direction='right' />
          </button>
        </nav>
      </header>

      <div
        className='column__body'
        ref={(el) => {
          bodyRef.current = el;
          scrollRef(el);
        }}
        onScroll={(e) => onScroll(e.currentTarget)}
      >
        {error
          ? <p className='column__error'>{error}</p>
          : data && (
            <article className={`chapter${loading ? ' chapter--stale' : ''}`}>
              {data.verses.map((verse) => (
                <span
                  key={verse.n}
                  data-verse={verse.n}
                  className={`verse${verse.n === refValue.verse ? ' verse--target' : ''}`}
                >
                  <sup className='verse__number'>{verse.n}</sup>
                  {verse.text}
                  {' '}
                </span>
              ))}
            </article>
          )}
      </div>
    </section>
  );
}

const Chevron = ({ direction }: { direction: 'left' | 'right' }) => (
  <svg viewBox='0 0 16 16' aria-hidden='true'>
    <path d={direction === 'left' ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'} />
  </svg>
);

const Dice = () => (
  <svg viewBox='0 0 16 16' aria-hidden='true'>
    <rect x='2.5' y='2.5' width='11' height='11' rx='2.5' />
    <circle cx='5.8' cy='5.8' r='.95' fill='currentColor' stroke='none' />
    <circle cx='10.2' cy='10.2' r='.95' fill='currentColor' stroke='none' />
    <circle cx='8' cy='8' r='.95' fill='currentColor' stroke='none' />
  </svg>
);

const Plus = () => (
  <svg viewBox='0 0 16 16' aria-hidden='true'>
    <path d='M8 3.5v9M3.5 8h9' />
  </svg>
);

const Cross = () => (
  <svg viewBox='0 0 16 16' aria-hidden='true'>
    <path d='M4.5 4.5l7 7M11.5 4.5l-7 7' />
  </svg>
);
